import {
  ChatCalculationSource,
  ChatIodlSource,
} from "../FpPages/Chat/evidenceFooter";
import { openInteractionInViewer } from "../FpPages/RMS/Dashboard/Cards/openInViewer";
import { fetchArtifactItem } from "../FpPages/Artifacts/useArtifacts";
import { openArtifact } from "../FpPages/Artifacts/openArtifact";
import { figuresInProse } from "../FileViewer/citedTableCells";

// What an agentic-chat citation opens when activated, as one function, so the
// citation chip in the prose and the row in the answer's sources list
// (#11426) cannot drift apart. The target's open depends on which
// discriminator is set: a document (by blob id), an RMS record preview, an
// RMS interaction (its viewer), a structured-data source (the source viewer
// on the cited row), or a web URL (new tab).
export interface ChatCitationTarget {
  docId?: string;
  publicId?: string;
  kind?: string;
  // The single attribute the citation names, when it came from one value
  // rather than the record as a whole (#11369): the preview highlights it and
  // scrolls to it instead of opening on the top of the record.
  factName?: string;
  interactionId?: string;
  // An artifact citation (#11819): resolved by id and opened in the pane
  // exactly as the library opens it.
  artifactId?: string;
  webUrl?: string;
  // The structured-data citation's query and rows — see chatSourceIodl.
  iodlSource?: ChatIodlSource;
  iodlQid?: string;
  // A recorded calculation (#13618). It has no document to open: the chip
  // or row that carries it opens the calculation card beside itself, and
  // each input row there opens that input's own target.
  calculation?: ChatCalculationSource;
  // Evidence anchor for document citations (#10310): harvested page number +
  // verbatim chunk text, forwarded to the viewer as highlightPageNum /
  // highlightContext so the click lands on the cited passage.
  pageNum?: number;
  sourceContent?: string;
  displayLabel: string;
  // The cited figure, when the citation was bound to one in the prose.
  displayText?: string;
  // How the page prints the cited figure, when that differs from how it is
  // shown (#12452): a table cell holding `2.093` whose page prints `209.3%`.
  // The viewer highlights this spelling; the reader still sees displayText.
  highlightText?: string;
  // Highlight the cited value where the page prints it, or nothing — never
  // the passage fallback (#12090). Set by a table artifact cell, whose
  // passage is the whole source table: the fallback that tints about one
  // line for a chat claim lights up every row of the table for a cell whose
  // value the page never prints (a reworded, converted or computed value).
  exactOnly?: boolean;
  // Open a document on the cited passage alone: the text the citation shows
  // is not on the page, so the viewer is given no figure to look for and
  // tints the passage. Set by a table cell whose prose summarizes its clause
  // (#14543).
  passageOnly?: boolean;
  // The prose around the citation, minus other chips — the legacy fallback
  // for finding the cited passage when no harvested anchor exists. A thunk,
  // because it clones a DOM subtree and only the document branch wants it.
  context?: () => string;
  /** Company label for a filename-only numeric table citation. */
  rowLabel?: () => string;
  // The host page mounts its own `<FileViewer />`; ask Layout to fill the
  // shared viewer state without also mounting the global viewer.
  inlineViewerOnly?: boolean;
  // The citation sits inside an artifact pane (#12452): its document opens
  // beside the artifact rather than in its place, where the pane has room.
  fromArtifact?: boolean;
}

export const openChatCitation = (target: ChatCitationTarget): void => {
  // A calculation opens as a card anchored to whatever was clicked, which
  // only that host can draw; there is nothing for the page to open.
  if (target.calculation) return;
  const {
    docId,
    publicId,
    kind,
    factName,
    interactionId,
    artifactId,
    webUrl,
    iodlSource,
    iodlQid,
    pageNum,
    sourceContent,
    displayLabel,
    displayText,
    highlightText,
    exactOnly = false,
    passageOnly = false,
    context,
    rowLabel,
    inlineViewerOnly = false,
    fromArtifact = false,
  } = target;
  // What the viewer looks for on the page: its own spelling when known.
  const figure = passageOnly ? undefined : highlightText || displayText;

  // Artifact citation (#11819): the registry entry carries only the id, so
  // the item is fetched and opened through the library's own pane-detail
  // builder — a table as a table, a diagram as a diagram, a file as a file.
  // What it cited rides along so a table lands on the cell (#12818): the
  // figure the citation is bound to, or, for a chip bound to none, the
  // figures of the paragraph around it. Never its words, which would mark
  // every name it mentions.
  if (artifactId) {
    const bound = [displayText, highlightText].filter(
      (value, index, all): value is string =>
        Boolean(value?.trim()) && all.indexOf(value) === index
    );
    const citedValues =
      bound.length > 0 ? bound : figuresInProse(context?.() ?? "");
    void fetchArtifactItem(artifactId)
      .then((item) => openArtifact(item, citedValues))
      .catch((error: unknown) =>
        console.error("Artifact citation: artifact lookup failed", error)
      );
    return;
  }

  // Web citation: open the URL in a new tab. The URL is model-controlled
  // marker text, so only http(s) schemes may reach window.open.
  if (webUrl) {
    if (/^https?:\/\//i.test(webUrl)) {
      window.open(webUrl, "_blank", "noopener,noreferrer");
    }
    return;
  }

  // Structured-data citation: open the source viewer with this call's SQL and
  // the cited row, carried inline (no source-map re-fetch — an agentic turn
  // has no persisted legacy query answer to fetch by qid). #10389
  if (iodlSource) {
    window.dispatchEvent(
      new CustomEvent("openIodlSource", {
        detail: {
          sql: iodlSource.sql,
          columns: iodlSource.columns,
          rowCount: iodlSource.rowCount,
          reasoning: iodlSource.reasoning,
          rows: iodlSource.rows,
          qid: iodlQid,
          sourceLabel: iodlSource.sourceLabel,
        },
      })
    );
    return;
  }

  // RMS entity citation: open the entity preview via the same event the
  // legacy editor CitationNode uses for rmsEntity refs. A citation that
  // names one attribute carries it, so the preview highlights that attribute
  // and scrolls to it rather than opening on the top of the record (#11369).
  if (publicId && kind) {
    window.dispatchEvent(
      new CustomEvent("openEntityPreview", {
        detail: {
          entityKind: kind,
          entityId: publicId,
          factName,
        },
      })
    );
    return;
  }

  // RMS interaction citation: open the interaction as a source in the note
  // viewer — its backing document when it has one, the live note content
  // keyed by interaction id otherwise (#11496). Never navigates to the
  // editable record page.
  //
  // The anchor is CLAIM-first, unlike the document branch below: a
  // document's `sourceContent` is a small verbatim chunk near the claim,
  // but an interaction's harvested content is its whole structured
  // evidence — header, summary, and up to ~4KB of note text (#10694). As a
  // highlight context that tints one line per evidence sentence across the
  // entire note and drowns the figure's occurrence anchoring, so the
  // claim's own sentence (then the clicked figure) anchors instead, and
  // the evidence stays out of it.
  if (interactionId) {
    const surroundingClaim = context?.();
    void openInteractionInViewer(
      interactionId,
      displayLabel,
      {
        highlightContext: surroundingClaim || figure || undefined,
        originalNumValue: figure || undefined,
        parentElementText: surroundingClaim || undefined,
        exactOnly,
      },
      // The host page's own pane draws it; the global drawer stays out,
      // exactly as the document branch below asks.
      { inlineViewerOnly }
    );
    return;
  }

  // Document citation: open the cited document via the direct-open contract
  // the sidebar / selected-files use. Also pass the cited figure +
  // surrounding text so the FileViewer scrolls to and highlights the passage
  // in the PDF, the way the old chat did (Layout forwards these to
  // setFileViewerData).
  const surrounding = context?.();
  const filenameOnly = sourceContent === displayLabel;
  const evidence = filenameOnly ? undefined : sourceContent;
  const citationRowLabel =
    filenameOnly && pageNum == null && displayText && /\d/.test(displayText)
      ? rowLabel?.() || undefined
      : undefined;
  window.dispatchEvent(
    new CustomEvent("openDocumentPanel", {
      detail: {
        fileId: docId,
        fileName: displayLabel,
        fileType: "file",
        isDirectFileOpen: true,
        // Prefer the harvested evidence anchor: the verbatim chunk text
        // (which actually exists in the document) over the cited figure /
        // the model's paraphrase, plus the page to jump to (#10310).
        highlightContext: evidence || figure || surrounding || undefined,
        citationRowLabel,
        highlightPageNum: pageNum ?? undefined,
        originalNumValue: figure || undefined,
        parentElementText: surrounding || undefined,
        exactOnly,
        inlineViewerOnly,
        fromArtifact,
      },
    })
  );
};
